"use client";

import { createContext, type Dispatch, type SetStateAction, useContext } from "react";
import type { AddressSuggestion } from "@/features/geo/shared/address-types";
import type { PostalCode } from "@/features/geo/shared/postal-codes";
import type { State } from "@/features/quote/shared/quote-config";
import type { StepId } from "@/features/quote/shared/wizard-steps";

export type ListField = "preferences" | "timeSlots" | "callTimes";

// Step components under ./steps/ read the wizard's state and handlers from
// here instead of having 20 props drilled through. The value is built fresh
// on every render in QuoteWizard - it must NOT be memoized, since handlers
// (autoAdvance, goToStep) close over the current step.
export type WizardContextValue = {
  state: State;
  setState: Dispatch<SetStateAction<State>>;
  update: <K extends keyof State>(key: K, value: State[K]) => void;
  step: number;
  goToStep: (n: number) => void;
  autoAdvance: (delay?: number) => void;
  stepReady: (id: StepId) => boolean;
  isBusiness: boolean;
  fieldErrors: Record<string, string>;
  setFieldError: (field: string, error: string | null) => void;
  validateEmailField: (value: string) => string | null;
  validatePhoneField: (value: string) => string | null;
  toggle: <K extends ListField>(field: K, value: State[K][number]) => void;
  handleAddressSelect: (address: AddressSuggestion) => void;
  handleSkipAddress: () => void;
  handleResetAddress: () => void;
  bbrLoading: boolean;
  bbrError: string | null;
  honeypot: string;
  setHoneypot: (value: string) => void;
  submitError: string | null;
  isSubmitting: boolean;
  handleSubmit: () => void;
  postalMatch: PostalCode | null;
};

const WizardContext = createContext<WizardContextValue | null>(null);

export const WizardProvider = WizardContext.Provider;

export function useWizard(): WizardContextValue {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error("useWizard skal bruges inden i en WizardProvider");
  return ctx;
}
